// All 12 chromatic notes
const CHROMATIC_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Scale formulas defined as semitone intervals from the root
// W = Whole step (2 semitones), H = Half step (1 semitone)
const SCALE_FORMULAS = {
  'Major': [2, 2, 1, 2, 2, 2, 1],                    // W-W-H-W-W-W-H
  'Natural Minor': [2, 1, 2, 2, 1, 2, 2],            // W-H-W-W-H-W-W
  'Harmonic Minor': [2, 1, 2, 2, 1, 3, 1],           // W-H-W-W-H-W+H-H
  'Major Pentatonic': [2, 2, 3, 2, 3],               // W-W-m3-W-m3
  'Minor Pentatonic': [3, 2, 2, 3, 2],               // m3-W-W-m3-W
  'Blues Major': [2, 1, 1, 3, 2, 3],                 // W-H-H-m3-W-m3
  'Blues Minor': [3, 2, 1, 1, 3, 2],                 // m3-W-H-H-m3-W
  'Ionian': [2, 2, 1, 2, 2, 2, 1],                   // Same as Major
  'Dorian': [2, 1, 2, 2, 2, 1, 2],                   // W-H-W-W-W-H-W
  'Phrygian': [1, 2, 2, 2, 1, 2, 2],                 // H-W-W-W-H-W-W
  'Lydian': [2, 2, 2, 1, 2, 2, 1],                   // W-W-W-H-W-W-H
  'Mixolydian': [2, 2, 1, 2, 2, 1, 2],               // W-W-H-W-W-H-W
  'Aeolian': [2, 1, 2, 2, 1, 2, 2],                  // Same as Natural Minor
  'Locrian': [1, 2, 2, 1, 2, 2, 2],                  // H-W-W-H-W-W-W
};

/**
 * Gets the index of a note in the chromatic scale
 * @param {string} note - The note name (e.g., 'C', 'C#', 'D')
 * @returns {number} The index of the note (0-11)
 */
function getNoteIndex(note) {
  return CHROMATIC_NOTES.indexOf(note);
}

/**
 * Gets a note at a specific index in the chromatic scale
 * @param {number} index - The index in the chromatic scale
 * @returns {string} The note name
 */
function getNoteAtIndex(index) {
  return CHROMATIC_NOTES[index % 12];
}

/**
 * Generates a scale based on a root note and scale type
 * @param {string} root - The root note (e.g., 'C', 'D#', 'F#')
 * @param {string} scaleType - The type of scale (e.g., 'Major', 'Minor Pentatonic')
 * @returns {string[]} Array of notes in the scale
 */
export function generateScale(root, scaleType) {
  const formula = SCALE_FORMULAS[scaleType];

  if (!formula) {
    throw new Error(`Unknown scale type: ${scaleType}`);
  }

  const rootIndex = getNoteIndex(root);

  if (rootIndex === -1) {
    throw new Error(`Unknown root note: ${root}`);
  }

  const scale = [root]; // Start with the root note
  let currentIndex = rootIndex;

  // Apply each interval in the formula
  for (const interval of formula) {
    currentIndex = (currentIndex + interval) % 12;
    scale.push(getNoteAtIndex(currentIndex));
  }

  // Remove the last note as it's the octave (root note again)
  scale.pop();

  return scale;
}

/**
 * Generates all scales for a given root note
 * @param {string} root - The root note
 * @returns {Object[]} Array of scale objects with root, type, and notes
 */
export function generateScalesForRoot(root) {
  return Object.keys(SCALE_FORMULAS).map(scaleType => ({
    root,
    type: scaleType,
    notes: generateScale(root, scaleType)
  }));
}

/**
 * Generates all scales for all root notes
 * @param {string[]} roots - Array of root notes (defaults to all chromatic notes)
 * @returns {Object[]} Array of all scale objects
 */
export function generateAllScales(roots = CHROMATIC_NOTES) {
  const allScales = [];

  for (const root of roots) {
    allScales.push(...generateScalesForRoot(root));
  }

  return allScales;
}

/**
 * Gets available scale types
 * @returns {string[]} Array of scale type names
 */
export function getScaleTypes() {
  return Object.keys(SCALE_FORMULAS);
}

/**
 * Adds a custom scale formula
 * @param {string} name - The name of the scale
 * @param {number[]} formula - Array of semitone intervals
 */
export function addScaleFormula(name, formula) {
  SCALE_FORMULAS[name] = formula;
}
